// proposalTypes.ts — types, helpers, role hook and API calls for the Proposals module.
// Mirrors models/proposal.go and the controllers in controllers/proposal*.go.

import { useEffect, useRef, useState } from 'react';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
import { userProfileStore } from '../../../stateManagers/stores/userProfileStore';
import { C } from '../../../components/GlobalComponents/uiTokens';

// ── Types ─────────────────────────────────────────────────────────────────────
export type ProposalChannel = 'email' | 'whatsapp';
export type ProposalStatus = 'sent' | 'accepted' | 'rejected';
/** What the card shows: the stored status, or "expired" (worked out by the server). */
export type ProposalView = ProposalStatus | 'expired';

export interface UserRef {
  id: string;
  email?: string | null;
  role?: string | null;
}

/** A file on a template / a sent proposal (models.FileAttachment + the CDN url). */
export interface FileAttachment {
  path: string;
  url: string;
  file_name: string;
  size: number;
  mime_type?: string;
}

export interface ProposalCategory {
  id: string;
  agency_id: string;
  name: string;
  slug: string;
  description?: string | null;
  template_count: number;
  created_at: string;
  updated_at: string;
}

export interface ProposalCategoryRef {
  id: string;
  name: string;
  slug: string;
}

export interface ProposalTemplate {
  id: string;
  agency_id: string;
  category_id: string;
  category?: ProposalCategoryRef | null;
  name: string;
  channel: ProposalChannel;
  subject: string;
  body: string;
  /** placeholder names found in subject + body (lower-case) */
  variables: string[];
  attachments: FileAttachment[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Proposal {
  id: string;
  agency_id: string;
  lead_id: string;
  template_id: string;
  template_name: string;
  channel: ProposalChannel;
  to_email?: string | null;
  to_phone?: string | null;
  subject?: string;
  body: string;
  attachments: FileAttachment[];
  amount?: number | null;
  valid_till?: string | null;
  status: ProposalStatus;
  sent_by?: UserRef | null;
  sent_at: string;
  created_at: string;
  updated_at?: string | null;
  is_expired: boolean;
}

/** What the send form posts — the same body for preview and send. */
export interface ProposalRequest {
  template_id: string;
  variables?: Record<string, string>;
  amount?: number;
  /** "YYYY-MM-DD" */
  valid_till?: string;
}

export interface ProposalPreview {
  channel: ProposalChannel;
  template_id: string;
  subject: string;
  body: string;
  /** placeholders that still need a value */
  missing: string[];
  to_email?: string | null;
  to_phone?: string | null;
  amount?: number | null;
  valid_till?: string | null;
  attachments: FileAttachment[];
  whatsapp_url?: string;
}

// ── Placeholders ──────────────────────────────────────────────────────────────
/** Keep in sync with models.ProposalAutoVariables (the server also sends the live list). */
export const FALLBACK_AUTO_VARIABLES = ['lead_name', 'interested_in', 'city', 'agency_name', 'staff_name', 'amount', 'valid_till'];

/** Placeholders that have their own input on the send form (not a free-text box). */
export const FIELD_VARIABLES = ['amount', 'valid_till'];

/** Same pattern as helpers.placeholderRe on the server. */
export const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

/** Unique placeholder names in the given texts, lower-cased, in order of first appearance. */
export const extractVariables = (...texts: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  texts.forEach((t) => {
    Array.from(t.matchAll(PLACEHOLDER_RE)).forEach((m) => {
      const n = m[1].toLowerCase();
      if (!seen.has(n)) {
        seen.add(n);
        out.push(n);
      }
    });
  });
  return out;
};

/** "interested_in" → "Interested in" */
export const varLabel = (name: string): string => {
  const s = name.replace(/_/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : name;
};

// ── Status look ───────────────────────────────────────────────────────────────
export const STATUS_VIEW: Record<ProposalView, { label: string; color: string; bg: string; border: string }> = {
  sent: { label: 'SENT', color: C.teal, bg: 'rgba(17,85,80,0.07)', border: 'rgba(17,85,80,0.2)' },
  accepted: { label: 'ACCEPTED', color: '#15803d', bg: 'rgba(22,163,74,0.1)', border: 'rgba(22,163,74,0.3)' },
  rejected: { label: 'REJECTED', color: '#c62828', bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.3)' },
  expired: { label: 'EXPIRED', color: '#b45309', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.35)' },
};

export const statusView = (p: Pick<Proposal, 'status' | 'is_expired'>): ProposalView => (p.status === 'sent' && p.is_expired ? 'expired' : p.status);

// ── Formatting ────────────────────────────────────────────────────────────────
export const fmtDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
};

export const fmtDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'Asia/Kolkata' });
};

/** 4500000 → "₹45,00,000" · 1499.5 → "₹1,499.50" (same as helpers.FormatINR). */
export const fmtINR = (v: number): string => {
  const hasPaise = Math.abs(v - Math.round(v)) > 1e-9;
  return (
    '₹' +
    v.toLocaleString('en-IN', {
      minimumFractionDigits: hasPaise ? 2 : 0,
      maximumFractionDigits: 2,
    })
  );
};

export const fmtSize = (bytes?: number): string => {
  if (!bytes || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** A local Date → "YYYY-MM-DD" (the day the person picked, not the UTC day). */
export const dateKey = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// ── Errors ────────────────────────────────────────────────────────────────────
export const apiError = (e: any, fallback: string): string => e?.response?.data?.error || e?.response?.data?.message || e?.message || fallback;

/** Placeholder names a 422 "Fill in every placeholder" response says are still missing. */
export const apiMissing = (e: any): string[] => {
  const m = e?.response?.data?.missing;
  return Array.isArray(m) ? m.filter((x: unknown): x is string => typeof x === 'string') : [];
};

// ── API ───────────────────────────────────────────────────────────────────────
interface ListRes<T> {
  success: boolean;
  data: T[];
}
interface OneRes<T> {
  success: boolean;
  message?: string;
  data: T;
}
interface TemplateListRes extends ListRes<ProposalTemplate> {
  /** placeholders the server fills itself */
  auto_variables?: string[];
}
interface SendRes extends OneRes<Proposal> {
  /** WhatsApp proposals only: wa.me link to open the chat */
  whatsapp_url?: string;
}

// File uploads can take a while; the default client timeout is shorter.
const UPLOAD_TIMEOUT_MS = 60000;
const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' }, timeout: UPLOAD_TIMEOUT_MS };

export const proposalApi = {
  categories: {
    list: () => apiClient.get<ListRes<ProposalCategory>>('/v1/proposal-categories').then((r) => r.data),
    create: (body: { name: string; slug?: string; description?: string }) =>
      apiClient.post<OneRes<ProposalCategory>>('/v1/proposal-categories', body).then((r) => r.data),
    /** Send only the fields that changed. description "" clears it. */
    update: (id: string, body: { name?: string; slug?: string; description?: string }) =>
      apiClient.patch<OneRes<ProposalCategory>>(`/v1/proposal-categories/${id}`, body).then((r) => r.data),
    remove: (id: string) => apiClient.delete(`/v1/proposal-categories/${id}`).then((r) => r.data),
  },

  templates: {
    list: (params?: { category_id?: string; channel?: ProposalChannel; is_active?: boolean; q?: string }) =>
      apiClient.get<TemplateListRes>('/v1/proposal-templates', { params }).then((r) => r.data),
    get: (id: string) => apiClient.get<OneRes<ProposalTemplate> & { auto_variables?: string[] }>(`/v1/proposal-templates/${id}`).then((r) => r.data),
    /** multipart: name, category_id, channel, subject, body, is_active, attachments[] */
    create: (form: FormData) => apiClient.post<OneRes<ProposalTemplate>>('/v1/proposal-templates', form, MULTIPART).then((r) => r.data),
    /** multipart: any of the fields above, plus remove_attachments[] (file paths) */
    update: (id: string, form: FormData) => apiClient.patch<OneRes<ProposalTemplate>>(`/v1/proposal-templates/${id}`, form, MULTIPART).then((r) => r.data),
    remove: (id: string) => apiClient.delete(`/v1/proposal-templates/${id}`).then((r) => r.data),
  },

  lead: {
    list: (leadId: string) => apiClient.get<ListRes<Proposal>>(`/v1/leads/${leadId}/proposals`).then((r) => r.data),
    preview: (leadId: string, body: ProposalRequest) =>
      apiClient.post<OneRes<ProposalPreview>>(`/v1/leads/${leadId}/proposals/preview`, body).then((r) => r.data),
    send: (leadId: string, body: ProposalRequest) =>
      apiClient.post<SendRes>(`/v1/leads/${leadId}/proposals`, body, { timeout: 30000 }).then((r) => r.data),
    setStatus: (proposalId: string, status: ProposalStatus) =>
      apiClient.patch<OneRes<Proposal>>(`/v1/proposals/${proposalId}/status`, { status }).then((r) => r.data),
  },
};

// ── Role ──────────────────────────────────────────────────────────────────────
/**
 * Who is looking. Reads the profile the app already loads (userProfileStore);
 * if it is missing it asks for it once.
 *   roleKnown=false → show a spinner, fetch nothing, show nothing admin-only
 *   profileError    → the profile could not be loaded
 */
export const useProposalRole = (): { isAdmin: boolean; roleKnown: boolean; profileError: string | null } => {
  const { profile, loading, fetchProfile } = userProfileStore();
  const asked = useRef(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (profile || loading || asked.current) return;
    asked.current = true;
    Promise.resolve(fetchProfile())
      .catch(() => undefined)
      .finally(() => setFailed(true));
  }, [profile, loading, fetchProfile]);

  const role = profile?.role ?? null;
  return {
    isAdmin: role === 'admin',
    roleKnown: !!role,
    profileError: !role && failed && !loading ? 'Please refresh the page and try again.' : null,
  };
};
